// ResumeCheckoutPrompt — the "you were about to buy this" step after signup.
//
// A logged-out buyer who clicks a paid plan on /pricing is sent through
// signup first. Email confirmation opens a new tab straight into /app, so
// without this step the purchase they started was silently dropped. The
// pending intent (lib/pendingIntent.ts) survives that tab switch; Dashboard
// renders this prompt from it once the user is signed in.
//
// Deliberately an explicit click, never an automatic redirect to Stripe: the
// user has just confirmed an email, not agreed to pay, and Stripe's hosted
// page is itself a further confirmation step before any charge.

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { capture } from "../lib/analytics";
import { requestCheckoutUrl } from "../lib/checkout";
import { BillingInterval, PaidPlanIntent, pricingUrlForIntent } from "../lib/pendingIntent";
import { useFocusTrap } from "../hooks/useFocusTrap";

interface ResumeCheckoutPromptProps {
  plan: PaidPlanIntent;
  interval: BillingInterval;
  onClose: () => void;
  /** "signup" (default): the intent survived email confirmation.
   *  "checkout_cancel": the user just left Stripe without paying. */
  variant?: "signup" | "checkout_cancel";
  /** Injected for tests; defaults to a real browser redirect. */
  redirect?: (url: string) => void;
}

const PLAN_LABEL: Record<PaidPlanIntent, string> = { scout: "Scout", pro: "Pro" };

const ResumeCheckoutPrompt: React.FC<ResumeCheckoutPromptProps> = ({ plan, interval, onClose, redirect, variant = "signup" }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trapRef = useFocusTrap<HTMLDivElement>({ onEscape: onClose });

  const handleContinue = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const target = await requestCheckoutUrl(plan, interval);
      const via = target.via === "portal" ? "resume_portal" : variant === "checkout_cancel" ? "resume_cancel" : "resume";
      capture("checkout_started", { plan, interval, via });
      (redirect ?? ((url: string) => { window.location.href = url; }))(target.url);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Checkout failed — please retry");
      setBusy(false);
    }
  };

  const handleCompare = () => {
    onClose();
    navigate(pricingUrlForIntent({ plan, interval }));
  };

  return (
    <div className="auth-backdrop" onClick={onClose}>
      <div
        className="upgrade-modal"
        onClick={(e) => e.stopPropagation()}
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="resume-checkout-title"
      >
        <button className="auth-close" onClick={onClose} aria-label="Close">×</button>
        <h2 className="upgrade-title" id="resume-checkout-title">
          {variant === "checkout_cancel" ? `Pick up your ${PLAN_LABEL[plan]} trial where you left off?` : `Finish upgrading to ${PLAN_LABEL[plan]}`}
        </h2>
        {variant === "checkout_cancel" ? (
          <p className="upgrade-reason">
            You left checkout before finishing. If it was the card step: your card is not charged until the 14-day
            trial ends, and you can cancel from Settings at any time before then. If something else stopped you,
            reply to any Parcely email and a person will answer.
          </p>
        ) : (
          <p className="upgrade-reason">
            Your account is ready. You picked <strong>{PLAN_LABEL[plan]}</strong>, billed{" "}
            {interval === "annual" ? "annually" : "monthly"}. Continue to our secure Stripe checkout to
            finish — nothing is charged until you confirm there.
          </p>
        )}
        {error && <p className="county-picker-error" role="alert">{error}</p>}
        <div className="upgrade-actions">
          <button type="button" className="upgrade-btn-secondary" onClick={handleCompare}>
            Compare plans
          </button>
          <button type="button" className="upgrade-btn-primary" onClick={handleContinue} disabled={busy}>
            {busy ? "Redirecting…" : variant === "checkout_cancel" ? "Back to checkout" : "Continue to checkout"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResumeCheckoutPrompt;
